import type { ComponentProps, Context, ReactNode } from 'react';
import { Suspense } from 'react';
import { QueryErrorBoundary } from '@gonfalon/error-boundaries/QueryErrorBoundary';
import type { QueryKey, UseSuspenseQueryOptions, UseSuspenseQueryResult } from '@tanstack/react-query';
import { useSuspenseQuery } from '@tanstack/react-query';

type SuspenseQueryProps<QueryFnData, Error, Data extends QueryFnData, Key extends QueryKey> = {
  context?: Context<Data>;
  query: UseSuspenseQueryOptions<QueryFnData, Error, Data, Key>;
  children: (props: UseSuspenseQueryResult<Data, Error>) => ReactNode;
  fallback?: ReactNode;
} & (
  | {
      boundary: ComponentProps<typeof QueryErrorBoundary>['fallbackRender'];
      errorSeverity: ComponentProps<typeof QueryErrorBoundary>['severity'];
      owner: ComponentProps<typeof QueryErrorBoundary>['owner'];
      errorIgnoredStatuses: ComponentProps<typeof QueryErrorBoundary>['ignoredStatuses'];
    }
  | {
      boundary?: never;
      errorSeverity?: never;
      owner?: never;
      errorIgnoredStatuses?: never;
    }
);

/**
 * A wrapper component that combines React Suspense with TanStack Query's useSuspenseQuery
 * to provide declarative data fetching with built-in loading states and error boundaries.
 *
 * @example
 * ```tsx
 * <SuspenseQuery
 *   query={{
 *     queryKey: ['user', userId],
 *     queryFn: () => fetchUser(userId)
 *   }}
 *   fallback={<Spinner />}
 *   boundary={({ error, resetErrorBoundary }) => (
 *     <div>
 *       <p>Failed to load user: {error.message}</p>
 *       <button onClick={resetErrorBoundary}>Retry</button>
 *     </div>
 *   )}
 *   errorSeverity="medium"
 *   owner="security-team"
 *   errorIgnoredStatuses={[404, 403]}
 * >
 *   {({ data }) => (
 *     <UserProfile user={data} />
 *   )}
 * </SuspenseQuery>
 * ```
 */
export function SuspenseQuery<QueryFnData, Error, Data extends QueryFnData, Key extends QueryKey>({
  fallback,
  boundary,
  errorSeverity,
  owner,
  errorIgnoredStatuses,
  ...props
}: SuspenseQueryProps<QueryFnData, Error, Data, Key>) {
  const content =
    fallback !== undefined ? (
      <Suspense fallback={fallback}>
        <SuspenseQueryContent {...props} />
      </Suspense>
    ) : (
      <SuspenseQueryContent {...props} />
    );

  return boundary ? (
    <QueryErrorBoundary
      fallbackRender={boundary}
      severity={errorSeverity}
      owner={owner}
      ignoredStatuses={errorIgnoredStatuses}
    >
      {content}
    </QueryErrorBoundary>
  ) : (
    content
  );
}

function SuspenseQueryContent<QueryFnData, Error, Data extends QueryFnData, Key extends QueryKey>({
  query,
  children,
  context: QueryContext,
}: Omit<
  SuspenseQueryProps<QueryFnData, Error, Data, Key>,
  'fallback' | 'boundary' | 'errorSeverity' | 'owner' | 'errorIgnoredStatuses'
>) {
  const result = useSuspenseQuery(query);
  return QueryContext ? <QueryContext value={result.data}>{children(result)}</QueryContext> : children(result);
}
